'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Monitor,
  Tablet,
  Smartphone,
  Eye,
  EyeOff,
  Save,
  UploadCloud,
  RotateCcw,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Undo2,
  Redo2,
  ExternalLink,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Files,
  Settings2,
  Share2,
  History,
  SearchCheck,
} from 'lucide-react';
import type { SectionCategory } from '@/lib/storefront/section-templates';
import {
  createStarterPage,
  makeSection,
  normalizeBuilderPage,
  type BuilderPage,
  type PageSection,
} from '@/lib/storefront/section-library';
import { pageBackgroundStyle, pageDesignCssVars, pageFontFaceCss } from '@/lib/storefront/page-settings';
import { pageNameFromSlug } from '@/lib/site/page-slugs';
import { siteLinkOptions, collectLinkIssues, type SitePageLink } from '@/lib/site/site-links';
import { buildSeoChecklist } from '@/lib/storefront/seo-checklist';
import { createSectionId } from '@/lib/storefront/section-templates';
import { SectionCanvas } from './SectionCanvas';
import { SectionPicker } from './SectionPicker';
import { SectionSettingsPanel } from './SectionSettingsPanel';
import { PageManager } from './PageManager';
import { PageSettingsPanel } from './PageSettingsPanel';
import { ImportCustomSectionModal } from './ImportCustomSectionModal';
import { SeoChecklistPanel } from './SeoChecklistPanel';
import { RevisionHistoryPanel } from './RevisionHistoryPanel';
import { ZOOM_PRESETS, type PreviewZoom } from './PreviewFrame';
import { storefrontHref } from '@/components/platform/format';
import { StorefrontDataProvider } from '@/lib/storefront/storefront-data';
import { api } from '@/lib/api';
import { apiAsset } from '@/config';
import type { Vehicle } from '@/types';

type Device = 'desktop' | 'tablet' | 'mobile';
type Banner = { kind: 'success' | 'error'; text: string } | null;

interface SiteBuilderProps {
  agency: {
    slug: string;
    name: string;
    customDomain?: string | null;
    phone?: string;
    email?: string;
    whatsapp?: string;
    address?: string;
    hours?: string;
    logoUrl?: string;
  };
  pageSlug: string;
  onSelectPage: (slug: string) => void;
}

const DEVICE_OPTIONS: { id: Device; icon: React.ComponentType<{ className?: string }>; label: string }[] = [
  { id: 'desktop', icon: Monitor, label: 'Desktop' },
  { id: 'tablet', icon: Tablet, label: 'Tablet' },
  { id: 'mobile', icon: Smartphone, label: 'Mobile' },
];

const SEGMENT_TRACK =
  'inline-flex h-8 items-center gap-0.5 rounded-lg border border-line bg-shell p-0.5';

const TOOL_BTN =
  'inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-line bg-card px-2.5 text-xs font-semibold text-ink transition-colors hover:bg-shell focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand disabled:pointer-events-none disabled:opacity-40';

function segmentButtonClass(active: boolean, compact = false): string {
  return [
    'inline-flex h-7 items-center justify-center rounded-md text-[11px] font-semibold transition-colors',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand',
    'disabled:pointer-events-none disabled:opacity-35',
    compact ? 'w-7 px-0' : 'min-w-[2.25rem] px-2',
    active ? 'bg-card text-ink shadow-sm' : 'text-muted hover:bg-card/70 hover:text-ink',
  ].join(' ');
}

export function SiteBuilder({ agency, pageSlug, onSelectPage }: SiteBuilderProps) {
  const fallbackName = pageSlug === 'home' ? 'Home' : pageNameFromSlug(pageSlug);
  const platformBase = `/api/platform/tenants/${agency.slug}`;
  const siteConfigPath = (suffix = '') => `${platformBase}/site-config${suffix}`;
  const siteImagesPath = `${platformBase}/site-images`;
  const draftUrl = `${platformBase}/site-config/${pageSlug}/draft`;
  const siteHref = storefrontHref({ slug: agency.slug, customDomain: agency.customDomain ?? null });
  const viewSiteHref = pageSlug === 'home' ? siteHref : `${siteHref.replace(/\/$/, '')}/${pageSlug}`;

  const agencyBrand = useMemo(
    () => ({
      name: agency.name,
      phone: agency.phone,
      email: agency.email,
      whatsapp: agency.whatsapp ?? agency.phone,
      address: agency.address,
      hours: agency.hours,
      logoUrl: agency.logoUrl,
    }),
    [agency.address, agency.email, agency.hours, agency.logoUrl, agency.name, agency.phone, agency.whatsapp],
  );

  const [page, setPage] = useState<BuilderPage>(() => createStarterPage(fallbackName, agencyBrand));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [device, setDevice] = useState<Device>('desktop');
  const [zoom, setZoom] = useState<PreviewZoom>('fit');
  const [preview, setPreview] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(true);
  const [mobileLibraryOpen, setMobileLibraryOpen] = useState(false);
  const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false);
  const [pageManagerOpen, setPageManagerOpen] = useState(false);
  const [pageSettingsOpen, setPageSettingsOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [seoOpen, setSeoOpen] = useState(false);
  const [revisionsOpen, setRevisionsOpen] = useState(false);
  const [insertIndex, setInsertIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [sharingPreview, setSharingPreview] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [banner, setBanner] = useState<Banner>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [fleetVehicles, setFleetVehicles] = useState<Vehicle[]>([]);
  const [fleetReady, setFleetReady] = useState(false);
  const [sitePages, setSitePages] = useState<SitePageLink[]>([]);
  const [sitePagesReady, setSitePagesReady] = useState(false);

  const past = useRef<BuilderPage[]>([]);
  const future = useRef<BuilderPage[]>([]);
  const pageRef = useRef(page);
  const [historyTick, setHistoryTick] = useState(0);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  const selectedSection = useMemo(
    () => page.sections.find((section) => section.id === selectedId) ?? null,
    [page.sections, selectedId],
  );

  const storefrontData = useMemo(
    () => ({
      vehicles: fleetVehicles,
      vehiclesReady: fleetReady,
      preview: true,
      agency: agencyBrand,
    }),
    [agencyBrand, fleetVehicles, fleetReady],
  );

  const linkOptions = useMemo(() => siteLinkOptions(sitePages), [sitePages]);

  const markBanner = useCallback((next: Banner) => {
    setBanner(next);
    if (next) window.setTimeout(() => setBanner(null), 4000);
  }, []);

  // ── Load draft ─────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(draftUrl, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(`Could not load your draft (${res.status})`);
        const data = await res.json();
        if (cancelled) return;
        // An empty saved page is still a saved page: only fall back to the
        // starter layout when the API has no page row at all.
        const normalized = Array.isArray(data?.sections) ? normalizeBuilderPage(data) : null;
        setPage(normalized ?? createStarterPage(fallbackName, agencyBrand));
        setLoaded(true);
      } catch (error) {
        if (cancelled) return;
        markBanner({ kind: 'error', text: error instanceof Error ? error.message : 'Could not load your draft' });
        setPage(createStarterPage(fallbackName, agencyBrand));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [agencyBrand, draftUrl, markBanner, fallbackName]);

  // ── Load live fleet for dynamic sections ───────────────────
  useEffect(() => {
    let cancelled = false;
    setFleetReady(false);

    api<Vehicle[]>(`${platformBase}/vehicles`)
      .then((list) => {
        if (cancelled) return;
        setFleetVehicles(
          Array.isArray(list)
            ? list.map((vehicle) => ({
                ...vehicle,
                imageUrl: apiAsset(vehicle.imageUrl ?? ''),
                galleryImages: Array.isArray(vehicle.galleryImages)
                  ? vehicle.galleryImages.map((url) => apiAsset(url))
                  : [],
              }))
            : [],
        );
      })
      .catch(() => {
        if (!cancelled) setFleetVehicles([]);
      })
      .finally(() => {
        if (!cancelled) setFleetReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [platformBase]);

  // ── Load page list for the link picker ─────────────────────
  const loadSitePages = useCallback(async () => {
    try {
      const res = await fetch(`${platformBase}/site-pages`, { headers: { Accept: 'application/json' } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !Array.isArray(data?.pages)) return;
      setSitePages(
        data.pages
          .map((row: any): SitePageLink => ({
            slug: String(row?.slug ?? ''),
            name: String(row?.name ?? ''),
            isPublished: Boolean(row?.isPublished),
          }))
          .filter((page: SitePageLink) => page.slug.length > 0),
      );
      setSitePagesReady(true);
    } catch {
      // The picker still offers the built-in storefront links.
    }
  }, [platformBase]);

  useEffect(() => {
    void loadSitePages();
  }, [loadSitePages]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    if (!dirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ── History ────────────────────────────────────────────────
  const commit = useCallback((next: BuilderPage) => {
    past.current = [...past.current.slice(-40), page];
    future.current = [];
    setPage(next);
    setDirty(true);
    setSaveError(null);
    setHistoryTick((tick) => tick + 1);
  }, [page]);

  const undo = useCallback(() => {
    const previous = past.current.pop();
    if (!previous) return;
    future.current = [page, ...future.current].slice(0, 40);
    setPage(previous);
    setDirty(true);
    setHistoryTick((tick) => tick + 1);
  }, [page]);

  const redo = useCallback(() => {
    const [next, ...rest] = future.current;
    if (!next) return;
    future.current = rest;
    past.current = [...past.current, page];
    setPage(next);
    setDirty(true);
    setHistoryTick((tick) => tick + 1);
  }, [page]);

  // ── Section operations ─────────────────────────────────────
  const addSection = useCallback(
    (category: SectionCategory, variationId: string) => {
      // Header and footer are site-wide: only the home page owns them.
      if (pageSlug !== 'home' && (category === 'header' || category === 'footer')) return;
      const section = makeSection(category, variationId, agencyBrand);
      if (!section) return;
      const index = insertIndex ?? page.sections.length;
      const sections = [...page.sections];
      sections.splice(Math.min(Math.max(index, 0), sections.length), 0, section);
      commit({ ...page, sections, updatedAt: new Date().toISOString() });
      setSelectedId(section.id);
      setMobileLibraryOpen(false);
      setInsertIndex(null);
    },
    [agencyBrand, commit, insertIndex, page, pageSlug],
  );

  const insertImportedSection = useCallback(
    (section: PageSection, warnings: string[]) => {
      const index = insertIndex ?? page.sections.length;
      const sections = [...page.sections];
      sections.splice(Math.min(Math.max(index, 0), sections.length), 0, section);
      commit({ ...page, sections, updatedAt: new Date().toISOString() });
      setSelectedId(section.id);
      setMobileLibraryOpen(false);
      setInsertIndex(null);
      if (warnings.length > 0) {
        markBanner({ kind: 'success', text: `Custom section imported with ${warnings.length} warning(s).` });
      } else {
        markBanner({ kind: 'success', text: 'Custom section imported.' });
      }
    },
    [commit, insertIndex, markBanner, page],
  );

  /** One library surface: docked pane on lg+, drawer below. Never both. */
  const openSectionLibrary = useCallback((atIndex: number | null = null) => {
    setInsertIndex(atIndex);
    if (typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches) {
      setLibraryOpen(true);
      setMobileLibraryOpen(false);
      return;
    }
    setMobileLibraryOpen(true);
  }, []);

  const closeSectionLibrary = useCallback(() => {
    setLibraryOpen(false);
    setMobileLibraryOpen(false);
    setInsertIndex(null);
  }, []);

  const moveSection = useCallback(
    (id: string, direction: -1 | 1) => {
      const index = page.sections.findIndex((section) => section.id === id);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= page.sections.length) return;
      const sections = [...page.sections];
      const [moved] = sections.splice(index, 1);
      sections.splice(target, 0, moved);
      commit({ ...page, sections, updatedAt: new Date().toISOString() });
    },
    [commit, page],
  );

  const reorderSection = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
      if (fromIndex >= page.sections.length || toIndex >= page.sections.length) return;
      const sections = [...page.sections];
      const [moved] = sections.splice(fromIndex, 1);
      sections.splice(toIndex, 0, moved);
      commit({ ...page, sections, updatedAt: new Date().toISOString() });
      setSelectedId(moved.id);
    },
    [commit, page],
  );

  const duplicateSection = useCallback(
    (id: string) => {
      const index = page.sections.findIndex((section) => section.id === id);
      if (index < 0) return;
      const source = page.sections[index];
      const newId = createSectionId();
      const content = JSON.parse(JSON.stringify(source.content)) as Record<string, unknown>;
      if (source.category === 'custom') {
        const oldWrapperId = typeof content.wrapperId === 'string' && content.wrapperId
          ? content.wrapperId
          : source.id;
        content.wrapperId = newId;
        if (typeof content.scopedCss === 'string' && oldWrapperId !== newId) {
          content.scopedCss = content.scopedCss.split(`.sb-custom-${oldWrapperId}`).join(`.sb-custom-${newId}`);
        }
      }
      const copy: PageSection = {
        ...source,
        id: newId,
        content,
        style: { ...source.style },
      };
      const sections = [...page.sections];
      sections.splice(index + 1, 0, copy);
      commit({ ...page, sections, updatedAt: new Date().toISOString() });
      setSelectedId(copy.id);
    },
    [commit, page],
  );

  const resetSection = useCallback(
    (id: string) => {
      const index = page.sections.findIndex((section) => section.id === id);
      if (index < 0) return;
      const current = page.sections[index];
      const fresh = makeSection(current.category, current.variationId, agencyBrand);
      if (!fresh) return;
      if (!window.confirm('Reset this section to its default content and style?')) return;
      const sections = [...page.sections];
      sections[index] = { ...fresh, id: current.id };
      commit({ ...page, sections, updatedAt: new Date().toISOString() });
      setSelectedId(current.id);
    },
    [agencyBrand, commit, page],
  );

  const removeSection = useCallback(
    (id: string) => {
      if (!window.confirm('Delete this section? You can undo afterward.')) return;
      const sections = page.sections.filter((section) => section.id !== id);
      commit({ ...page, sections, updatedAt: new Date().toISOString() });
      if (selectedId === id) setSelectedId(null);
    },
    [commit, page, selectedId],
  );

  const selectSection = useCallback((id: string) => {
    setSelectedId(id);
    setSettingsOpen(true);
    // Only open the settings drawer below xl; the docked pane handles desktop.
    if (typeof window !== 'undefined' && !window.matchMedia('(min-width: 1280px)').matches) {
      setMobileSettingsOpen(true);
    }
  }, []);

  const updateContent = useCallback(
    (content: Record<string, any>) => {
      if (!selectedId) return;
      const sections = page.sections.map((section) =>
        section.id === selectedId ? { ...section, content } : section,
      );
      commit({ ...page, sections, updatedAt: new Date().toISOString() });
    },
    [commit, page, selectedId],
  );

  const updateStyle = useCallback(
    (style: PageSection['style']) => {
      if (!selectedId) return;
      const sections = page.sections.map((section) =>
        section.id === selectedId ? { ...section, style } : section,
      );
      commit({ ...page, sections, updatedAt: new Date().toISOString() });
    },
    [commit, page, selectedId],
  );

  const updateStyleById = useCallback(
    (sectionId: string, style: PageSection['style']) => {
      const sections = page.sections.map((section) =>
        section.id === sectionId ? { ...section, style } : section,
      );
      commit({ ...page, sections, updatedAt: new Date().toISOString() });
    },
    [commit, page],
  );

  // ── Persistence ────────────────────────────────────────────
  const saveDraft = useCallback(async (options?: { silent?: boolean }) => {
    setSaving(true);
    setSaveError(null);
    try {
      const snapshot = JSON.stringify({
        name: page.name,
        sections: page.sections,
        settings: page.settings ?? {},
        designTokens: page.designTokens ?? {},
      });
      const res = await fetch(siteConfigPath(`/${pageSlug}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: pageSlug,
          slug: pageSlug,
          name: page.name || (pageSlug === 'home' ? 'Home' : pageSlug),
          sections: page.sections,
          settings: page.settings ?? {},
          design_tokens: page.designTokens ?? {},
          version: 1,
          updatedAt: new Date().toISOString(),
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message =
          data?.errors && typeof data.errors === 'object'
            ? Object.values(data.errors).flat().join(' ')
            : typeof data?.error === 'string'
              ? data.error
              : typeof data?.message === 'string'
                ? data.message
                : `Save failed (${res.status})`;
        throw new Error(message);
      }
      // Edits made while the request was in flight are not part of this save.
      const current = pageRef.current;
      const unchanged = JSON.stringify({
        name: current.name,
        sections: current.sections,
        settings: current.settings ?? {},
        designTokens: current.designTokens ?? {},
      }) === snapshot;
      if (unchanged) setDirty(false);
      if (!options?.silent) markBanner({ kind: 'success', text: 'Draft saved' });
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Save failed';
      setSaveError(message);
      markBanner({ kind: 'error', text: message });
      return false;
    } finally {
      setSaving(false);
    }
  }, [markBanner, page, pageSlug]);

  // Autosave a few seconds after the last edit. A failed save blocks further
  // attempts until the user edits again or saves manually, so a broken draft
  // does not retry forever.
  useEffect(() => {
    if (!dirty || !loaded || loading || saving || publishing || reverting || saveError) return;
    const timer = window.setTimeout(() => {
      void saveDraft({ silent: true });
    }, 4000);
    return () => window.clearTimeout(timer);
  }, [dirty, loaded, loading, saving, publishing, reverting, saveError, saveDraft]);

  const sharePreview = useCallback(async () => {
    setSharingPreview(true);
    try {
      // The preview endpoint reads the saved draft, so flush pending edits first.
      if (dirty) {
        const saved = await saveDraft({ silent: true });
        if (!saved) return;
      }

      const res = await fetch(`${platformBase}/site-preview`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || typeof data?.token !== 'string') {
        throw new Error(typeof data?.error === 'string' ? data.error : `Could not create a preview link (${res.status})`);
      }

      const base = siteHref.replace(/\/$/, '');
      const path = pageSlug === 'home' ? '/' : `/${pageSlug}`;
      const url = `${base}${path}?draftPreview=${encodeURIComponent(data.token)}`;

      try {
        await navigator.clipboard.writeText(url);
        markBanner({ kind: 'success', text: 'Private preview link copied — it expires in 7 days.' });
      } catch {
        window.prompt('Copy this private preview link:', url);
      }
    } catch (error) {
      markBanner({ kind: 'error', text: error instanceof Error ? error.message : 'Could not create a preview link' });
    } finally {
      setSharingPreview(false);
    }
  }, [dirty, markBanner, pageSlug, platformBase, saveDraft, siteHref]);

  const publish = useCallback(async () => {
    const linkIssues = sitePagesReady ? collectLinkIssues(page.sections, sitePages) : [];
    if (linkIssues.length > 0) {
      const summary = linkIssues
        .slice(0, 4)
        .map((issue) => `${issue.href} (${issue.kind})`)
        .join(', ');
      const more = linkIssues.length > 4 ? ` and ${linkIssues.length - 4} more` : '';
      if (!window.confirm(
        `This page links to ${linkIssues.length} unpublished or missing page(s): ${summary}${more}. Visitors will see a 404. Publish anyway?`,
      )) {
        return;
      }
    }

    const seoErrors = buildSeoChecklist(page, pageSlug, sitePages).filter((issue) => issue.severity === 'error');
    if (seoErrors.length > 0) {
      const summary = seoErrors.slice(0, 3).map((issue) => issue.message).join(' ');
      if (!window.confirm(
        `SEO checklist found ${seoErrors.length} error(s): ${summary} Publish anyway?`,
      )) {
        return;
      }
    }

    setPublishing(true);
    try {
      const saved = await saveDraft();
      if (!saved) return;
      const res = await fetch(siteConfigPath(`/${pageSlug}/publish`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data?.error === 'string' ? data.error : `Publish failed (${res.status})`);
      markBanner({
        kind: 'success',
        text: pageSlug === 'home'
          ? 'Home page published — the changes are live.'
          : `“${page.name || pageSlug}” published — the page is live.`,
      });
    } catch (error) {
      markBanner({ kind: 'error', text: error instanceof Error ? error.message : 'Publish failed' });
    } finally {
      setPublishing(false);
    }
  }, [markBanner, page, pageSlug, saveDraft, sitePages, sitePagesReady]);

  const revert = useCallback(async () => {
    if (!window.confirm('Discard the current draft and restore the last published version?')) return;
    setReverting(true);
    try {
      const res = await fetch(siteConfigPath(`/${pageSlug}/revert`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data?.error === 'string' ? data.error : `Revert failed (${res.status})`);
      const normalized = normalizeBuilderPage(data);
      if (normalized) {
        setPage(normalized);
        setSelectedId(null);
        setDirty(false);
      }
      past.current = [];
      future.current = [];
      setHistoryTick((tick) => tick + 1);
      markBanner({ kind: 'success', text: 'Draft restored to the published version' });
    } catch (error) {
      markBanner({ kind: 'error', text: error instanceof Error ? error.message : 'Revert failed' });
    } finally {
      setReverting(false);
    }
  }, [markBanner, pageSlug]);

  const busy = saving || publishing || reverting || sharingPreview;
  const sharedCategories: SectionCategory[] = pageSlug === 'home' ? [] : ['header', 'footer'];
  const canUndo = past.current.length > 0;
  const canRedo = future.current.length > 0;
  const canvasFontCss = pageFontFaceCss(page.designTokens ?? {});
  const canvasCustomCss = typeof page.designTokens?.customCss === 'string'
    ? page.designTokens.customCss.trim()
    : '';
  const pageTitle = pageSlug === 'home' ? 'Home page' : (page.name || fallbackName);
  const saveStatus = saving
    ? { label: 'Saving…', tone: 'bg-brand/15 text-brand', live: 'polite' as const }
    : saveError
      ? { label: 'Save failed', tone: 'bg-danger/15 text-danger', live: 'assertive' as const }
      : dirty
        ? { label: 'Unsaved', tone: 'bg-warning/15 text-warning', live: 'polite' as const }
        : { label: 'Saved', tone: 'bg-success/15 text-success', live: 'polite' as const };

  if (loading) {
    return (
      <div className="dashboard-root flex h-full min-h-[420px] items-center justify-center bg-shell">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-7 w-7 animate-spin text-brand" />
          <p className="text-sm text-muted">Loading the site builder…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-root flex h-dvh min-h-[560px] w-full min-w-0 flex-col overflow-hidden bg-shell text-ink">
      <header className="shrink-0 border-b border-line bg-card px-3 py-2 sm:px-4">
        <div className="flex min-w-0 flex-col gap-2 xl:flex-row xl:items-center xl:gap-3">
          {/* Identity */}
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-2.5">
            <Link
              to={`/super-admin/agencies/${agency.slug}`}
              onClick={(event) => {
                if (dirty && !window.confirm('You have unsaved changes. Leave the builder and lose them?')) {
                  event.preventDefault();
                }
              }}
              className="inline-flex h-8 shrink-0 items-center rounded-lg border border-line px-2.5 text-xs font-semibold text-muted transition-colors hover:bg-shell hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand"
            >
              ← Agency
            </Link>

            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted">Site builder</p>
              <div className="flex min-w-0 items-baseline gap-1.5">
                <h1 className="truncate text-sm font-semibold leading-tight text-ink">{pageTitle}</h1>
                {agency.name ? (
                  <>
                    <span className="hidden text-muted sm:inline" aria-hidden>
                      ·
                    </span>
                    <span className="hidden truncate text-xs font-normal text-muted sm:inline" title={agency.name}>
                      {agency.name}
                    </span>
                  </>
                ) : null}
              </div>
            </div>

            <span
              role="status"
              aria-live={saveStatus.live}
              title={saveError ?? undefined}
              className={`inline-flex h-6 shrink-0 items-center rounded-full px-2 text-[10px] font-semibold ${saveStatus.tone}`}
            >
              {saving ? <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden /> : null}
              {saveStatus.label}
            </span>

            <button
              type="button"
              onClick={() => setPageManagerOpen(true)}
              className={TOOL_BTN}
              title="Manage site pages"
              aria-label="Manage pages"
            >
              <Files className="h-3.5 w-3.5" aria-hidden />
              <span className="hidden sm:inline">Pages</span>
            </button>
          </div>

          {/* Preview + actions */}
          <div className="flex min-w-0 flex-wrap items-center gap-1.5 sm:gap-2 xl:justify-end">
            <div className={SEGMENT_TRACK} role="group" aria-label="Preview device">
              {DEVICE_OPTIONS.map(({ id, icon: Icon, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setDevice(id)}
                  className={segmentButtonClass(device === id, true)}
                  title={`${label} preview`}
                  aria-label={`${label} preview`}
                  aria-pressed={device === id}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden />
                </button>
              ))}
            </div>

            <div className={SEGMENT_TRACK} role="group" aria-label="Canvas zoom">
              {ZOOM_PRESETS.map(({ id, label }) => (
                <button
                  key={String(id)}
                  type="button"
                  onClick={() => setZoom(id)}
                  className={segmentButtonClass(zoom === id)}
                  title={`Zoom ${label}`}
                  aria-label={`Zoom ${label}`}
                  aria-pressed={zoom === id}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className={SEGMENT_TRACK} role="group" aria-label="Edit history">
              <button
                type="button"
                onClick={undo}
                disabled={!canUndo}
                data-history={historyTick}
                className={segmentButtonClass(false, true)}
                title="Undo"
                aria-label="Undo"
              >
                <Undo2 className="h-3.5 w-3.5" aria-hidden />
              </button>
              <button
                type="button"
                onClick={redo}
                disabled={!canRedo}
                className={segmentButtonClass(false, true)}
                title="Redo"
                aria-label="Redo"
              >
                <Redo2 className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>

            <span className="mx-0.5 hidden h-5 w-px bg-line sm:block" aria-hidden />

            <button
              type="button"
              onClick={() => setPreview((value) => !value)}
              className={`${TOOL_BTN} ${preview ? 'border-ink bg-ink text-shell hover:bg-ink/90 hover:text-shell' : ''}`}
              title={preview ? 'Return to editing' : 'Preview without chrome'}
              aria-pressed={preview}
            >
              {preview ? <EyeOff className="h-3.5 w-3.5" aria-hidden /> : <Eye className="h-3.5 w-3.5" aria-hidden />}
              <span className="hidden sm:inline">{preview ? 'Editing' : 'Preview'}</span>
            </button>

            <a
              href={viewSiteHref}
              target="_blank"
              rel="noreferrer"
              className={TOOL_BTN}
              title="Open the live website"
              aria-label="View live site"
            >
              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              <span className="hidden lg:inline">View site</span>
            </a>

            <button
              type="button"
              onClick={() => void sharePreview()}
              disabled={busy}
              className={TOOL_BTN}
              title="Copy a private draft preview link"
              aria-label="Copy preview link"
            >
              {sharingPreview ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Share2 className="h-3.5 w-3.5" aria-hidden />}
              <span className="hidden xl:inline">Preview link</span>
            </button>

            <button
              type="button"
              onClick={() => {
                setSaveError(null);
                setPageSettingsOpen(true);
              }}
              className={TOOL_BTN}
              title="Page settings, SEO and design"
              aria-label="Page settings"
            >
              <Settings2 className="h-3.5 w-3.5" aria-hidden />
              <span className="hidden xl:inline">Page settings</span>
            </button>

            <button
              type="button"
              onClick={() => setSeoOpen(true)}
              className={TOOL_BTN}
              title="SEO checklist"
              aria-label="SEO checklist"
            >
              <SearchCheck className="h-3.5 w-3.5" aria-hidden />
              <span className="hidden 2xl:inline">SEO</span>
            </button>

            <button
              type="button"
              onClick={() => setRevisionsOpen(true)}
              className={TOOL_BTN}
              title="Revision history"
              aria-label="Revision history"
            >
              <History className="h-3.5 w-3.5" aria-hidden />
              <span className="hidden 2xl:inline">History</span>
            </button>

            <button
              type="button"
              onClick={revert}
              disabled={busy}
              className={TOOL_BTN}
              title="Discard draft and reload the current published version"
              aria-label="Revert to published"
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
              <span className="hidden lg:inline">Revert</span>
            </button>

            <span className="mx-0.5 hidden h-5 w-px bg-line md:block" aria-hidden />

            <button
              type="button"
              onClick={() => void saveDraft()}
              disabled={busy}
              className={TOOL_BTN}
              title="Save draft"
              aria-label="Save draft"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Save className="h-3.5 w-3.5" aria-hidden />}
              <span className="hidden sm:inline">Save draft</span>
            </button>

            <button
              type="button"
              onClick={() => void publish()}
              disabled={busy}
              className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg bg-brand px-3 text-xs font-semibold text-white transition-colors hover:bg-brand/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand disabled:pointer-events-none disabled:opacity-40"
              title="Publish this page"
              aria-label="Publish"
            >
              {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <UploadCloud className="h-3.5 w-3.5" aria-hidden />}
              Publish
            </button>
          </div>
        </div>
      </header>

      {banner && (
        <div
          className={`flex items-center gap-2 border-b px-4 py-2 text-xs font-semibold ${
            banner.kind === 'success'
              ? 'border-success/30 bg-success/10 text-success'
              : 'border-danger/30 bg-danger/10 text-danger'
          }`}
        >
          {banner.kind === 'success' ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
          {banner.text}
        </div>
      )}

      {pageSlug !== 'home' && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-sky-500/30 bg-sky-500/10 px-4 py-2 text-xs text-sky-200">
          <p>
            <span className="font-semibold">Shared header and footer.</span>{' '}
            Every page uses the navigation and footer built on the Home page.
          </p>
          <button
            type="button"
            onClick={() => {
              if (dirty && !window.confirm('You have unsaved changes on this page. Open Home and lose them?')) return;
              onSelectPage('home');
            }}
            className="rounded-md border border-sky-400/40 bg-shell px-2 py-1 text-[11px] font-semibold text-sky-100 hover:bg-card"
          >
            Edit Home
          </button>
        </div>
      )}

      {/* Workspace: docked panes on xl/lg, drawers below */}
      <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
        {libraryOpen && !preview && (
          <aside className="scrollbar-none hidden w-[min(290px,28vw)] shrink-0 overflow-y-auto border-r border-line bg-card lg:block">
            <SectionPicker
              onAdd={addSection}
              onClose={closeSectionLibrary}
              hiddenCategories={sharedCategories}
              insertHint={insertIndex}
              onImportCustom={() => setImportOpen(true)}
            />
          </aside>
        )}

        <main className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line bg-card px-3 py-1.5">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => {
                  if (window.matchMedia('(min-width: 1024px)').matches) {
                    if (libraryOpen) {
                      closeSectionLibrary();
                    } else {
                      openSectionLibrary(null);
                    }
                  } else {
                    openSectionLibrary(null);
                  }
                }}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-muted hover:bg-shell hover:text-ink"
                title={libraryOpen ? 'Hide the section library' : 'Show the section library'}
              >
                {libraryOpen ? <PanelLeftClose className="h-3.5 w-3.5" /> : <PanelLeftOpen className="h-3.5 w-3.5" />}
                Library
              </button>
            </div>

            <p className="text-[11px] text-muted">
              {page.sections.length} section{page.sections.length === 1 ? '' : 's'}
            </p>

            <button
              type="button"
              onClick={() => {
                if (window.matchMedia('(min-width: 1280px)').matches) {
                  setSettingsOpen((value) => !value);
                } else {
                  setMobileSettingsOpen(true);
                  setSettingsOpen(true);
                }
              }}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-muted hover:bg-shell hover:text-ink"
              title={settingsOpen ? 'Hide settings' : 'Show settings'}
            >
              Settings
              {settingsOpen ? <PanelRightClose className="h-3.5 w-3.5" /> : <PanelRightOpen className="h-3.5 w-3.5" />}
            </button>
          </div>

          <div
            className="flex min-h-0 min-w-0 flex-1 flex-col bg-white text-slate-900"
            style={pageDesignCssVars(page.designTokens) as React.CSSProperties}
            data-storefront-page
          >
            {canvasFontCss ? <style data-builder-fonts>{canvasFontCss}</style> : null}
            {canvasCustomCss ? <style data-builder-css>{canvasCustomCss}</style> : null}
            <StorefrontDataProvider value={storefrontData}>
              <SectionCanvas
                sections={page.sections}
                selectedId={selectedId}
                previewMode={preview}
                deviceWidth={device}
                zoom={zoom}
                pageStyle={pageBackgroundStyle(page.settings) as React.CSSProperties}
                lockedCategories={sharedCategories}
                onSelect={selectSection}
                onMove={moveSection}
                onReorder={reorderSection}
                onDuplicate={duplicateSection}
                onReset={resetSection}
                onRemove={removeSection}
                onChangeStyle={updateStyleById}
                onAddAt={(index) => openSectionLibrary(index)}
              />
            </StorefrontDataProvider>
          </div>
        </main>

        {settingsOpen && !preview && (
          <aside className="scrollbar-none hidden w-[min(340px,30vw)] shrink-0 overflow-y-auto border-l border-line bg-card xl:block">
            <SectionSettingsPanel
              section={selectedSection}
              onChangeContent={updateContent}
              onChangeStyle={updateStyle}
              uploadUrl={siteImagesPath}
              linkOptions={linkOptions}
              sitePages={sitePages}
              pagesReady={sitePagesReady}
              previewBreakpoint={device}
            />
          </aside>
        )}
      </div>

      {/* Library drawer — only below lg; docked pane handles desktop */}
      {mobileLibraryOpen && !preview && (
        <div className="fixed inset-0 z-50 flex justify-start bg-slate-950/50 lg:hidden">
          <button type="button" className="absolute inset-0 cursor-default" aria-label="Close library" onClick={closeSectionLibrary} />
          <div className="relative z-10 flex h-full w-full max-w-md flex-col overflow-hidden bg-card shadow-2xl sm:max-w-sm">
            <SectionPicker
              onAdd={addSection}
              hiddenCategories={sharedCategories}
              onClose={closeSectionLibrary}
              insertHint={insertIndex}
              onImportCustom={() => setImportOpen(true)}
            />
          </div>
        </div>
      )}

      {/* Settings drawer (< xl) */}
      {mobileSettingsOpen && !preview && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/50 xl:hidden">
          <button type="button" className="absolute inset-0 cursor-default" aria-label="Close settings" onClick={() => setMobileSettingsOpen(false)} />
          <div className="relative z-10 flex h-full w-full max-w-md flex-col overflow-hidden border-l border-line bg-card shadow-2xl">
            <div className="flex items-center justify-between border-b border-line px-3 py-2">
              <p className="text-xs font-semibold text-ink">Section settings</p>
              <button type="button" onClick={() => setMobileSettingsOpen(false)} className="rounded-md px-2 py-1 text-[11px] font-semibold text-muted hover:bg-shell">
                Close
              </button>
            </div>
            <div className="scrollbar-none min-h-0 flex-1 overflow-y-auto">
              <SectionSettingsPanel
                section={selectedSection}
                onChangeContent={updateContent}
                onChangeStyle={updateStyle}
                uploadUrl={siteImagesPath}
                linkOptions={linkOptions}
                sitePages={sitePages}
                pagesReady={sitePagesReady}
                previewBreakpoint={device}
              />
            </div>
          </div>
        </div>
      )}

      {loaded && page.sections.length === 0 && (
        <div className="pointer-events-none fixed bottom-4 left-1/2 z-40 -translate-x-1/2 rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white shadow-lg">
          Add your first section from the library
        </div>
      )}

      {pageManagerOpen && (
        <PageManager
          agencySlug={agency.slug}
          agencyName={agency.name}
          agencyBrand={agencyBrand}
          currentSlug={pageSlug}
          dirty={dirty}
          onClose={() => {
            setPageManagerOpen(false);
            void loadSitePages();
          }}
          onSelectPage={onSelectPage}
          onSaveCurrent={saveDraft}
        />
      )}

      {pageSettingsOpen && (
        <PageSettingsPanel
          page={page}
          pageSlug={pageSlug}
          uploadUrl={siteImagesPath}
          saving={saving}
          saveError={saveError}
          onChange={(patch) => commit({ ...page, ...patch, updatedAt: new Date().toISOString() })}
          onSave={saveDraft}
          onClose={() => setPageSettingsOpen(false)}
        />
      )}

      <ImportCustomSectionModal
        open={importOpen}
        importUrl={`${platformBase}/site-section-import`}
        onClose={() => setImportOpen(false)}
        onImported={insertImportedSection}
      />

      <SeoChecklistPanel
        open={seoOpen}
        page={page}
        pageSlug={pageSlug}
        sitePages={sitePages}
        onClose={() => setSeoOpen(false)}
      />

      <RevisionHistoryPanel
        open={revisionsOpen}
        listUrl={`${platformBase}/site-config/${pageSlug}/revisions`}
        restoreUrl={(id) => `${platformBase}/site-config/${pageSlug}/revisions/${id}/restore`}
        onClose={() => setRevisionsOpen(false)}
        onRestored={(draft) => {
          const normalized = normalizeBuilderPage(draft);
          if (normalized) {
            setPage(normalized);
            setSelectedId(null);
            setDirty(true);
            past.current = [];
            future.current = [];
            setHistoryTick((tick) => tick + 1);
            markBanner({ kind: 'success', text: 'Revision restored into the draft. Publish when ready.' });
          }
        }}
      />
    </div>
  );
}
